/**
 * Inline trigger editor — one form per trigger kind (TIME / EVENT / CONDITION).
 *
 * Reads/writes a TriggerSpec; mounting/unmounting is the parent's job.
 *
 * Phase 1 keeps the condition trigger as a textarea-JSON editor — the flat
 * AND/OR row composer comes later in this same phase (step guards already
 * use the same AST). This is acceptable per the design doc's "structured
 * form-based editor, no canvas" constraint for the trigger types we
 * actually need today.
 */

import type { TriggerSpec, WorkflowEventType } from '@mana/shared';
import { WORKFLOW_EVENT_TYPES } from '@mana/shared';
import { Field, Input, Select, Textarea } from '../../ui/Input.js';

export function TriggerForm({
  trigger,
  onChange,
  availableStepKeys,
}: {
  trigger: TriggerSpec;
  onChange: (next: TriggerSpec) => void;
  availableStepKeys: string[];
}) {
  if (trigger.type === 'time') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label="Mode">
          <Select
            value={trigger.mode}
            onChange={(e) =>
              onChange({ ...trigger, mode: e.target.value as 'cron' | 'relative_offset' })
            }
          >
            <option value="cron">Cron schedule</option>
            <option value="relative_offset">Offset from a loan date</option>
          </Select>
        </Field>
        <Field label="Timezone">
          <Input
            value={trigger.timezone ?? 'America/Chicago'}
            onChange={(e) => onChange({ ...trigger, timezone: e.target.value })}
          />
        </Field>
        {trigger.mode === 'cron' && (
          <Field label="Cron expression" hint="e.g. 0 9 * * 1-5 (9am weekdays)">
            <Input
              value={trigger.cron ?? ''}
              onChange={(e) => onChange({ ...trigger, cron: e.target.value })}
              placeholder="0 9 * * 1-5"
            />
          </Field>
        )}
        {trigger.mode === 'relative_offset' && (
          <>
            <Field label="Anchor field">
              <Select
                value={trigger.anchorField ?? 'fundingDate'}
                onChange={(e) => onChange({ ...trigger, anchorField: e.target.value })}
              >
                <option value="lockDate">Lock date</option>
                <option value="fundingDate">Funding date</option>
                <option value="closingDateTime">Closing date</option>
                <option value="estimatedClosingDate">Estimated closing</option>
              </Select>
            </Field>
            <Field label="Offset (days)" hint="Positive = after, negative = before">
              <Input
                type="number"
                value={trigger.offsetDays ?? 0}
                onChange={(e) =>
                  onChange({ ...trigger, offsetDays: parseInt(e.target.value, 10) || 0 })
                }
              />
            </Field>
          </>
        )}
        <EntryStepKeyField
          value={trigger.entryStepKey}
          keys={availableStepKeys}
          onChange={(v) => onChange({ ...trigger, entryStepKey: v })}
        />
      </div>
    );
  }

  if (trigger.type === 'event') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label="Event">
          <Select
            value={trigger.event}
            onChange={(e) =>
              onChange({ ...trigger, event: e.target.value as WorkflowEventType })
            }
          >
            {WORKFLOW_EVENT_TYPES.map((evt) => (
              <option key={evt} value={evt}>
                {evt}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Match value" hint="Optional — e.g. status name">
          <Input
            value={trigger.matchValue ?? ''}
            onChange={(e) => onChange({ ...trigger, matchValue: e.target.value || undefined })}
          />
        </Field>
        <EntryStepKeyField
          value={trigger.entryStepKey}
          keys={availableStepKeys}
          onChange={(v) => onChange({ ...trigger, entryStepKey: v })}
        />
      </div>
    );
  }

  // condition
  const predicateJson = JSON.stringify(trigger.predicate, null, 2);
  return (
    <div className="space-y-3">
      <Field
        label="Predicate (JSON AST)"
        hint="Phase 1 ships a JSON view of the condition AST. The flat AND/OR row composer lands in step guards first; trigger composer is a Phase 2 follow-up."
      >
        <Textarea
          rows={6}
          value={predicateJson}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange({ ...trigger, predicate: parsed });
            } catch {
              // Ignore invalid JSON — let the user keep typing.
            }
          }}
          className="font-mono text-xs"
        />
      </Field>
      <EntryStepKeyField
        value={trigger.entryStepKey}
        keys={availableStepKeys}
        onChange={(v) => onChange({ ...trigger, entryStepKey: v })}
      />
    </div>
  );
}

function EntryStepKeyField({
  value,
  keys,
  onChange,
}: {
  value: string | undefined;
  keys: string[];
  onChange: (v: string | undefined) => void;
}) {
  return (
    <Field label="Entry step" hint="Which step does this trigger route into?">
      <Select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">(first step)</option>
        {keys.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function describeTrigger(t: TriggerSpec): string {
  if (t.type === 'time') {
    if (t.mode === 'cron') return `⏱ Cron ${t.cron ?? ''} (${t.timezone})`;
    return `⏱ ${(t.offsetDays ?? 0) >= 0 ? '+' : ''}${t.offsetDays ?? 0}d from ${t.anchorField ?? '—'}`;
  }
  if (t.type === 'event') {
    return `⚡ ${t.event}${t.matchValue ? ` = "${t.matchValue}"` : ''}`;
  }
  return `🧪 Condition predicate`;
}

export function triggerKindIcon(type: TriggerSpec['type']): string {
  switch (type) {
    case 'time':
      return '⏱';
    case 'event':
      return '⚡';
    case 'condition':
      return '🧪';
  }
}

export function defaultTriggerOfType(type: TriggerSpec['type']): TriggerSpec {
  switch (type) {
    case 'time':
      return {
        type: 'time',
        mode: 'relative_offset',
        anchorField: 'fundingDate',
        offsetDays: 30,
        timezone: 'America/Chicago',
      };
    case 'event':
      return { type: 'event', event: 'loan.status_changed' };
    case 'condition':
      return {
        type: 'condition',
        predicate: { op: 'exists', path: 'loan.lockDate' },
      };
  }
}
